import React, { useState, useEffect, useRef } from 'react';
import { Helmet } from 'react-helmet-async';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast, ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import * as XLSX from 'xlsx';
import {
  Container,
  Typography,
  Stack,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
  Button,
  RadioGroup,
  FormControlLabel,
  Radio,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  Tooltip,
  CircularProgress,
  Divider,
} from '@mui/material';
import { FaPlus, FaTimes, FaArrowLeft, FaFileExcel } from 'react-icons/fa';
import { getAllCourseDetails, getAllSubjectDetails, addChapterContent } from '../Servies/AdmiServices';

// The API's fileUrl is a relative path, e.g. "CR0179/Maths/5_1.pptx" — NOT a full URL.
// If a full CDN URL (or one still carrying a "pptdata/" prefix, or a leading slash) gets
// pasted/uploaded in, strip it down to just the part after "pptdata/".
const cleanRelativePptPath = (value) => {
  if (!value) return '';
  const trimmed = value.trim();
  const afterPptdata = trimmed.match(/pptdata\/+(.*)$/i);
  if (afterPptdata) return afterPptdata[1];
  return trimmed.replace(/^\/+/, '');
};

const emptyPptItem = () => ({ contentName: '', sequenceNo: '', fileUrl: '', contentKey: '' });

// Accepts a few header spellings per column so most exports/templates just work.
const HEADER_ALIASES = {
  contentName: ['contentname', 'content_name', 'name', 'title'],
  sequenceNo: ['sequenceno', 'sequence_no', 'sequence', 'seq', 'seqno'],
  fileUrl: ['fileurl', 'file_url', 'url', 'link', 'pptxurl', 'relativepath', 'relative_path', 'path'],
  contentKey: ['contentkey', 'content_key', 'key', 'password'],
};

const normalizeHeader = (header) => String(header || '').trim().toLowerCase().replace(/[^a-z0-9]/g, '');

const parsePptItemsWorkbook = (workbook) => {
  const sheet = workbook.Sheets[workbook.SheetNames[0]];
  const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' });

  return rows.map((row) => {
    const normalizedRow = {};
    Object.keys(row).forEach((key) => {
      normalizedRow[normalizeHeader(key)] = row[key];
    });

    const rowKeys = Object.keys(normalizedRow);
    const pick = (field) => {
      const aliases = HEADER_ALIASES[field];

      // Prefer an exact header match, checked in alias priority order, over a loose prefix
      // match — otherwise a generic column (e.g. "Key") appearing before the real one (e.g.
      // "Password") in the sheet would win and silently pull the wrong value.
      const exactAlias = aliases.find((alias) => normalizedRow[alias] !== undefined);
      if (exactAlias !== undefined) {
        return String(normalizedRow[exactAlias]).trim();
      }

      // Fallback: prefix match, e.g. "Content Key (optional)" -> "contentkeyoptional".
      const matchedKey = rowKeys.find((key) => aliases.some((alias) => key.startsWith(alias)));
      return matchedKey !== undefined ? String(normalizedRow[matchedKey]).trim() : '';
    };

    return {
      contentName: pick('contentName'),
      sequenceNo: pick('sequenceNo'),
      fileUrl: cleanRelativePptPath(pick('fileUrl')),
      contentKey: pick('contentKey'),
    };
  });
};

export default function AddChapterContent() {
  const navigate = useNavigate();
  const location = useLocation();
  const preselectedCourseId = location.state?.courseId || '';

  const [courses, setCourses] = useState([]);
  const [subjects, setSubjects] = useState([]);
  const [subjectsLoading, setSubjectsLoading] = useState(false);

  const [courseId, setCourseId] = useState('');
  const [subjectLevelId, setSubjectLevelId] = useState('');

  const [chapterMode, setChapterMode] = useState('new');
  const [chapterId, setChapterId] = useState('');
  const [chapterName, setChapterName] = useState('');
  const [chapterSequenceNo, setChapterSequenceNo] = useState('');

  const [pptItems, setPptItems] = useState([emptyPptItem()]);
  const [bulkUploading, setBulkUploading] = useState(false);
  const bulkFileInputRef = useRef(null);

  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  useEffect(() => {
    getAllCourseDetails()
      .then((res) => setCourses(res?.data || []))
      .catch(() => {});
  }, []);

  // Prefill the course when arriving from PPT Viewer's "Add Chapter" button.
  useEffect(() => {
    if (preselectedCourseId && courses.length > 0 && !courseId) {
      handleCourseChange(preselectedCourseId);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preselectedCourseId, courses]);

  const handleCourseChange = (value) => {
    setCourseId(value);
    setSubjectLevelId('');
    setSubjects([]);
    setChapterId('');
    setResult(null);
    if (!value) return;
    setSubjectsLoading(true);
    getAllSubjectDetails(value)
      .then((res) => setSubjects(res?.data || []))
      .catch(() => {})
      .finally(() => setSubjectsLoading(false));
  };

  // Manual "type directly" course id field: don't refetch/reset on every keystroke —
  // only look up its subjects once the admin finishes typing (on blur).
  const handleCourseIdTyped = (value) => {
    setCourseId(value);
  };

  const handleCourseIdBlur = () => {
    if (!courseId) return;
    setSubjectsLoading(true);
    getAllSubjectDetails(courseId)
      .then((res) => setSubjects(res?.data || []))
      .catch(() => {})
      .finally(() => setSubjectsLoading(false));
  };

  const handlePptItemChange = (index, field, value) => {
    setPptItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], [field]: value };
      return updated;
    });
  };

  // Cleans up a pasted full CDN URL (or one still carrying "pptdata/") down to the bare
  // relative path the API expects, e.g. "CR0179/Maths/5_1.pptx".
  const handleFileUrlBlur = (index) => {
    setPptItems((prev) => {
      const updated = [...prev];
      updated[index] = { ...updated[index], fileUrl: cleanRelativePptPath(updated[index].fileUrl) };
      return updated;
    });
  };

  const handleAddPptItem = () => {
    setPptItems((prev) => [...prev, emptyPptItem()]);
  };

  const handleRemovePptItem = (index) => {
    setPptItems((prev) => (prev.length > 1 ? prev.filter((_, i) => i !== index) : prev));
  };

  const handleBulkUploadClick = () => {
    bulkFileInputRef.current?.click();
  };

  const handleBulkFileChange = (event) => {
    const file = event.target.files[0];
    event.target.value = ''; // allow re-selecting the same file later
    if (!file) return;

    setBulkUploading(true);
    const reader = new FileReader();
    reader.onload = (e) => {
      try {
        const workbook = XLSX.read(e.target.result, { type: 'array' });
        const parsedRows = parsePptItemsWorkbook(workbook);
        const validRows = parsedRows.filter((row) => row.contentName && row.fileUrl);
        const skippedCount = parsedRows.length - validRows.length;

        if (validRows.length === 0) {
          toast.error('No valid rows found. Expected columns: Content Name, Sequence No, File URL (relative path), Content Key / Password.');
          return;
        }

        setPptItems(validRows);
        toast.success(`Loaded ${validRows.length} PPT item(s) from file.${skippedCount > 0 ? ` Skipped ${skippedCount} row(s) missing content name or file URL.` : ''}`);
      } catch (err) {
        toast.error('Failed to read the file. Please upload a valid Excel/CSV file.');
      } finally {
        setBulkUploading(false);
      }
    };
    reader.onerror = () => {
      setBulkUploading(false);
      toast.error('Failed to read the file.');
    };
    reader.readAsArrayBuffer(file);
  };

  const validate = () => {
    if (!courseId) return 'Please select a course.';
    if (!subjectLevelId) return 'Please select a subject.';
    if (chapterMode === 'new' && !chapterName.trim()) return 'Please enter a chapter name.';
    if (chapterMode === 'existing' && !chapterId) return 'Please enter an existing chapter id.';
    const validItems = pptItems.filter((item) => item.contentName.trim() && item.fileUrl.trim());
    if (validItems.length === 0) return 'Add at least one PPT item with a content name and file URL.';
    return '';
  };

  const handleSubmit = async () => {
    const validationError = validate();
    if (validationError) {
      toast.error(validationError);
      return;
    }

    const payload = {
      courseId,
      subjectLevelId: Number(subjectLevelId),
      chapterId: chapterMode === 'existing' ? Number(chapterId) : null,
      chapterName: chapterMode === 'new' ? chapterName.trim() : undefined,
      chapterSequenceNo: chapterSequenceNo ? Number(chapterSequenceNo) : undefined,
      pptItems: pptItems
        .filter((item) => item.contentName.trim() && item.fileUrl.trim())
        .map((item) => ({
          contentName: item.contentName.trim(),
          sequenceNo: item.sequenceNo ? Number(item.sequenceNo) : undefined,
          fileUrl: item.fileUrl.trim(),
          contentKey: item.contentKey.trim() || undefined,
        })),
    };

    setSubmitting(true);
    setResult(null);
    try {
      const res = await addChapterContent(payload);
      if (res?.data?.isSuccess) {
        toast.success(res.data.message || 'Chapter content added.');
        setResult(res.data);
      } else {
        toast.error(res?.data?.message || 'Failed to add chapter content.');
      }
    } catch (err) {
      toast.error('Failed to add chapter content.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <>
      <ToastContainer />
      <Helmet>
        <title> Dashboard: Add Chapter Content </title>
      </Helmet>

      <Container>
        <Stack direction="row" alignItems="center" spacing={2} mb={4}>
          <Tooltip title="Back">
            <IconButton onClick={() => navigate(-1)}>
              <FaArrowLeft />
            </IconButton>
          </Tooltip>
          <Typography variant="h4">Add Chapter Content (PPT)</Typography>
        </Stack>

        <Stack direction="row" spacing={2} flexWrap="wrap" rowGap={2} mb={1} alignItems="center">
          <FormControl size="small" sx={{ minWidth: 260 }}>
            <InputLabel id="course-label">Course</InputLabel>
            <Select
              labelId="course-label"
              label="Course"
              value={courseId}
              onChange={(e) => handleCourseChange(e.target.value)}
            >
              {courses.map((course) => (
                <MenuItem key={course.courseId} value={course.courseId}>
                  {course.courseDisplayName || course.courseId}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 260 }} disabled={!courseId || subjectsLoading}>
            <InputLabel id="subject-label">Subject</InputLabel>
            <Select
              labelId="subject-label"
              label="Subject"
              value={subjectLevelId}
              onChange={(e) => setSubjectLevelId(e.target.value)}
            >
              {subjects.map((subject) => (
                <MenuItem key={subject.levelId} value={subject.levelId}>
                  {subject.levelDisplayName || subject.levelId}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <Typography variant="body2" color="text.secondary">
            or type directly:
          </Typography>
        </Stack>

        <Stack direction="row" spacing={2} flexWrap="wrap" rowGap={2} mb={3}>
          <TextField
            size="small"
            label="Course Id"
            placeholder="e.g. CR0001"
            value={courseId}
            onChange={(e) => handleCourseIdTyped(e.target.value)}
            onBlur={handleCourseIdBlur}
            sx={{ minWidth: 260 }}
          />
          <TextField
            size="small"
            label="Subject Id"
            type="number"
            placeholder="e.g. 101"
            value={subjectLevelId}
            onChange={(e) => setSubjectLevelId(e.target.value)}
            sx={{ minWidth: 260 }}
          />
        </Stack>

        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          Chapter
        </Typography>
        <RadioGroup
          row
          value={chapterMode}
          onChange={(e) => setChapterMode(e.target.value)}
          sx={{ mb: 2 }}
        >
          <FormControlLabel value="new" control={<Radio />} label="Create new chapter" />
          <FormControlLabel value="existing" control={<Radio />} label="Add to existing chapter" />
        </RadioGroup>

        {chapterMode === 'new' ? (
          <Stack direction="row" spacing={2} flexWrap="wrap" rowGap={2} mb={3}>
            <TextField
              size="small"
              label="Chapter Name"
              value={chapterName}
              onChange={(e) => setChapterName(e.target.value)}
              sx={{ minWidth: 300 }}
            />
            <TextField
              size="small"
              label="Chapter Sequence No (optional)"
              type="number"
              value={chapterSequenceNo}
              onChange={(e) => setChapterSequenceNo(e.target.value)}
              sx={{ minWidth: 220 }}
            />
          </Stack>
        ) : (
          <Stack direction="row" spacing={2} mb={3}>
            <TextField
              size="small"
              label="Existing Chapter Id"
              type="number"
              value={chapterId}
              onChange={(e) => setChapterId(e.target.value)}
              sx={{ minWidth: 300 }}
            />
          </Stack>
        )}

        <Divider sx={{ mb: 3 }} />

        <Typography variant="subtitle1" fontWeight={700} gutterBottom>
          PPT Items
        </Typography>

        <TableContainer component={Paper} variant="outlined" sx={{ mb: 2 }}>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ bgcolor: 'grey.100' }}>
                <TableCell width={60}><strong>#</strong></TableCell>
                <TableCell><strong>Content Name</strong></TableCell>
                <TableCell width={140}><strong>Sequence No</strong></TableCell>
                <TableCell><strong>File URL (relative path)</strong></TableCell>
                <TableCell><strong>Content Key / Password (optional)</strong></TableCell>
                <TableCell width={48} />
              </TableRow>
            </TableHead>
            <TableBody>
              {pptItems.map((item, index) => (
                <TableRow key={index}>
                  <TableCell>{index + 1}</TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      placeholder="e.g. Introduction to Photosynthesis"
                      value={item.contentName}
                      onChange={(e) => handlePptItemChange(index, 'contentName', e.target.value)}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      type="number"
                      fullWidth
                      value={item.sequenceNo}
                      onChange={(e) => handlePptItemChange(index, 'sequenceNo', e.target.value)}
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Relative path only, not the full URL — e.g. CR0179/Maths/5_1.pptx">
                      <TextField
                        size="small"
                        fullWidth
                        placeholder="CR0179/Maths/5_1.pptx"
                        value={item.fileUrl}
                        onChange={(e) => handlePptItemChange(index, 'fileUrl', e.target.value)}
                        onBlur={() => handleFileUrlBlur(index)}
                      />
                    </Tooltip>
                  </TableCell>
                  <TableCell>
                    <TextField
                      size="small"
                      fullWidth
                      value={item.contentKey}
                      onChange={(e) => handlePptItemChange(index, 'contentKey', e.target.value)}
                    />
                  </TableCell>
                  <TableCell>
                    <Tooltip title="Remove row">
                      <span>
                        <IconButton
                          size="small"
                          onClick={() => handleRemovePptItem(index)}
                          disabled={pptItems.length === 1}
                        >
                          <FaTimes size={14} />
                        </IconButton>
                      </span>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>

        <Stack direction="row" spacing={2} alignItems="center" mb={4}>
          <Button variant="outlined" startIcon={<FaPlus />} onClick={handleAddPptItem}>
            Add Row
          </Button>

          <input
            type="file"
            accept=".xlsx,.xls,.csv"
            ref={bulkFileInputRef}
            style={{ display: 'none' }}
            onChange={handleBulkFileChange}
          />
          <Button
            variant="outlined"
            startIcon={!bulkUploading ? <FaFileExcel /> : null}
            onClick={handleBulkUploadClick}
            disabled={bulkUploading}
          >
            {bulkUploading ? <CircularProgress size={18} color="inherit" /> : 'Bulk Upload (Excel/CSV)'}
          </Button>
          <Typography variant="caption" color="text.secondary">
            Columns: Content Name, Sequence No, File URL (relative path, e.g. CR0179/Maths/5_1.pptx), Content Key / Password (optional) — replaces the rows above
          </Typography>
        </Stack>

        <Stack direction="row" justifyContent="flex-end" mb={4}>
          <Button variant="contained" onClick={handleSubmit} disabled={submitting}>
            {submitting ? <CircularProgress size={20} color="inherit" /> : 'Submit'}
          </Button>
        </Stack>

        {result && (
          <>
            <Divider sx={{ mb: 3 }} />
            <Typography variant="subtitle1" fontWeight={700} gutterBottom>
              Chapter Id: {result.chapterId}
            </Typography>
            <TableContainer component={Paper} variant="outlined">
              <Table size="small">
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.100' }}>
                    <TableCell><strong>Content Id</strong></TableCell>
                    <TableCell><strong>Content Name</strong></TableCell>
                    <TableCell><strong>Sequence No</strong></TableCell>
                    <TableCell><strong>File URL</strong></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(result.contents || []).map((content) => (
                    <TableRow key={content.contentId}>
                      <TableCell>{content.contentId}</TableCell>
                      <TableCell>{content.contentName}</TableCell>
                      <TableCell>{content.sequenceNo}</TableCell>
                      <TableCell sx={{ wordBreak: 'break-all' }}>{content.fileUrl}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </>
        )}
      </Container>
    </>
  );
}
